<script setup lang="ts">
/**
 * Mention « Paiement sécurisé » de la fiche produit — rendue SOUS les deux
 * colonnes, pleine largeur, HORS de toute carte.
 *
 * POURQUOI DEHORS : cette mention ne parle ni du produit ni de la boutique,
 * elle vaut pour tout paiement sur Donoor. Lui donner un volet au même rang
 * que « Composition » ou « Livraison » la faisait passer pour une information
 * produit, et donnait l'impression que la sécurité se « consultait » au lieu
 * d'être acquise. En pied de fiche, sans carte ni ombre, elle joue son vrai
 * rôle : un rappel discret de fin de page.
 *
 * ⚠️ ON NOMME LE PRESTATAIRE, ET SEULEMENT LE BON. `activeProviders` ne liste
 * que les prestataires réellement branchés sur CETTE boutique ET capables
 * d'encaisser (`connected && charges_enabled`) : afficher « PayPal » pour un
 * compte connecté mais non opérationnel promettrait un moyen de paiement que
 * l'acheteur ne verra pas au checkout. Repli neutre si rien n'est exposé.
 */
import type { PublicPaymentProviders } from "@donoor/types";

const { t } = useI18n();

const props = defineProps<{
  /**
   * Prestataires réellement branchés sur CETTE boutique. Annoncer « nos
   * prestataires » sans les nommer n'engage personne ; nommer celui qui
   * encaissera vraiment est ce qui rassure.
   */
  paymentProviders?: PublicPaymentProviders | null;
}>();

const activeProviders = computed<string[]>(() => {
  const p = props.paymentProviders;
  if (!p) return [];
  const list: string[] = [];
  if (p.stripe?.connected && p.stripe?.charges_enabled) list.push("Stripe");
  if (p.paypal?.connected && p.paypal?.charges_enabled) list.push("PayPal");
  return list;
});

const providerCount = computed(() => activeProviders.value.length);

/** « Stripe », « Stripe et PayPal », ou repli neutre si rien n'est exposé. */
const providerLabel = computed(() => {
  const list = activeProviders.value;
  if (list.length === 0) return t("boutique.productSecurityNote.prestatairesGeneriques");
  if (list.length === 1) return list[0] as string;
  return list.join(` ${t("boutique.productSecurityNote.jointureEt")} `);
});
</script>

<template>
  <div class="text-center">
    <p class="text-xs text-[#717198] dark:text-gray-500 leading-relaxed">
      <i18n-t keypath="boutique.productSecurityNote.phrase">
        <template #provider>
          <span class="font-semibold text-[#505870] dark:text-gray-300">{{ providerLabel }}</span>
        </template>
        <template #certification>
          {{ providerCount > 1 ? $t("boutique.productSecurityNote.prestatairesCertifies") : $t("boutique.productSecurityNote.prestataireCertifie") }}
        </template>
      </i18n-t>
    </p>
    <PublicPageSecurityBadges />
  </div>
</template>
